import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrawer } from 'src/hooks/useDrawer';
import { openChat } from 'src/utils';
import { menuItem } from 'src/constants/index';
import { Button } from 'src/components/Buttons/Button';
import { Separator } from 'src/components/Separator';
import classNames from 'classnames';
import { NavLink, useNavigate } from 'react-router';
import letter from 'src/assets/sidebar-icons/letter.png';
import { useApiContext, useGetBonuses, useGetFavoritesGames } from 'react-easyrocket';
import { gamesCategoriesSidebar } from 'src/constants/gameCategories';
import { Arrow } from 'src/components/svg/Arrows';
import { AccountForProfile } from 'src/components/svg/Accounts';
import { PWACards } from './components/PWACards';
import { GameCategoriesItem } from './components/GameCategoriesItem';
import { Banners } from 'src/components/Sidebar/components/SidebarContent/components/SidebarBanner/constant';
import { SidebarBanner } from './components/SidebarBanner';
import { useModalContext } from 'src/hooks/useModalContext';
import Languages from 'src/modules/locales/components/Languages';
import { useRoute } from 'src/router/hooks';
import '../../styles.css';
import { useGetCountOfTournaments } from 'src/hooks/useGetCountOfTournaments';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';
import { useGameCategories } from 'src/hooks/useFilterCategories';
import { SidebarLink } from 'src/pages/CatchGame/components/SidebarLink';
import { isShowCatchGame } from 'src/pages/CatchGame/helpers';
import { useGetBalance } from 'src/hooks/useGetBalance';
import { Clock } from 'src/components/svg/Clock';
import { useTimerDaysEvent } from 'src/utils/event24';
import { useGetEvent } from 'src/config/hooks/useGetEvent';
import { GPTButtonsBlock } from 'src/modules/gpt/components/GPTButtonsBlock';

export const SidebarContent: FC = () => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isCategories, setIsCategories] = useState(true);
  const { isAuthenticated } = useApiContext();
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { t } = useTranslation();
  const { closeDrawer } = useDrawer();
  const { data } = useGetBonuses();
  const { startRegistration, startLogin } = useAuthFlow();
  const { iconWithBalance } = useGetBalance();
  const event = useGetEvent();
  const eventName = event?.name;

  const { daysLeft, minutesLeft, hoursLeft, secondsLeft } = useTimerDaysEvent();

  const countOfTournaments = useGetCountOfTournaments();
  const filteringCategories = useGameCategories(gamesCategoriesSidebar);
  const { games: favoriteGames } = useGetFavoritesGames();

  const isFavouriteGames = favoriteGames.length && isAuthenticated;

  const preparedMenuItems =
    data && data?.length !== 0
      ? menuItem
      : menuItem.filter((item) => item.name !== 'menuItem.bonuses');

  const handeleLanguagesClick = useCallback(() => {
    menuRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [menuRef]);

  const navigateToBannerPage = useCallback(
    (isDriver: boolean) => {
      if (!isAuthenticated) {
        startRegistration();
        closeDrawer();

        return;
      }
      if (isDriver) {
        navigate(ROUTE.GAME.get('15347?name=Boomerang', ''));
        closeDrawer();
      }
      if (!isDriver) {
        navigate(ROUTE.SHOP.get());
        closeDrawer();
      }
    },
    [ROUTE.GAME, ROUTE.SHOP, closeDrawer, isAuthenticated, navigate, openModal]
  );

  return (
    <>
      <div className="flex flex-col items-center w-full gap-y-3 py-1">
        {isAuthenticated ? (
          <>
            <Button
              title={`${t('general.account')}`}
              mode="button-primary"
              className="w-full shadow-xl accountSidebarButton"
              onClick={() => {
                navigate(ROUTE.ACCOUNT_PROFILE_MAIN.get());
                closeDrawer();
              }}
              leftIcon={<AccountForProfile fill="var(--text_color_bage)" />}>
              {<p className="ml-3 uppercase">{t('general.account')}</p>}
            </Button>
            <Button
              mode="button-secondary"
              className="w-full text-b_secondary_text shadow-xl text-sm 768:text-base"
              onClick={() => {
                openModal('DEPOSIT');
                closeDrawer();
              }}
              title={`${t('general.deposit')}`}>
              {t('general.deposit')}
            </Button>
          </>
        ) : (
          <>
            <Button
              title={`${t('general.signUp')}`}
              mode="button-primary"
              onClick={() => {
                closeDrawer();
                startRegistration();
              }}>
              {t('general.signUp')}
            </Button>
            <Button
              title={`${t('general.logIn')}`}
              mode="button-secondary"
              onClick={() => {
                closeDrawer();
                startLogin();
              }}>
              {t('general.logIn')}
            </Button>
          </>
        )}
        <Separator width="w-full" />
        {isAuthenticated && (
          <>
            <div className="flex flex-col justify-center items-center">
              <span className="sidebar-heading">{t('general.balance')}</span>
              <p className="sidebar-balance">{iconWithBalance}</p>
            </div>
          </>
        )}
        {isAuthenticated ? <Separator width="w-full" noMarginTop /> : null}
        <GPTButtonsBlock />
        <Separator width="w-full" noMarginTop />
        <>
          <div
            className="w-full flex justify-around items-center cursor-pointer"
            onClick={() => {
              setIsCategories(!isCategories);
            }}>
            <p className="font-bold text-text_primary_sidebar uppercase">
              {t('general.gamesCategories')}
            </p>
            <Arrow
              fill="var(--b_secondary_text)"
              className={classNames(
                'w-[15px] h-[15px] color-text-2 stroke-[3px] text-end ease-in-out duration-300 stroke-text_primary_sidebar',
                !isCategories && 'origin-center rotate-180'
              )}
            />
          </div>
          {isCategories ? (
            <div className="w-full grid gap-[5px] text-xs font-black uppercase">
              {filteringCategories.map((item) => {
                if (!isFavouriteGames && item.path === 'favorites') return;
                return (
                  <div
                    key={item?.path}
                    onClick={() => {
                      closeDrawer();
                      navigate(ROUTE.GAMES.get(item?.path));
                    }}
                    className="cursor-pointer relative"
                    aria-label={`${t(item?.title)}`}>
                    {item.param === eventName && (
                      <p className="bg-[#943BED] w-[142px] lowercase shadow-timerSidebarShadow font-medium text-text_secondary_sidebar px-3 py-1 text-[10px] rounded-full leading-[9px] absolute right-3 -top-2 gap-1 flex flex-row items-center">
                        <Clock /> {daysLeft}d : {hoursLeft}h : {minutesLeft}m : {secondsLeft}s
                      </p>
                    )}
                    <GameCategoriesItem
                      image={item?.image}
                      content={item?.title}
                      param={item?.param}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </>

        <Separator width="w-[100%]" noMarginTop />
        {/* CATCH GAME LINK */}
        {isShowCatchGame() && (
          <>
            <SidebarLink />
            <Separator width="w-[100%]" noMarginTop />
          </>
        )}
        <div className="flex flex-col w-full">
          {preparedMenuItems.map((item, index) => {
            if (item.name === 'menuItem.dailyBonus') {
              return null;
            }

            if (item.name === 'menuItem.wheelOfFortune') {
              return null;
            }

            return (
              <NavLink
                aria-label={`${t(item.name)}`}
                to={ROUTE[item.path].get('', '')}
                key={index}
                className={({ isActive }) => {
                  const isHalloweenLink = item.name === 'menuItem.halloweenSpins';
                  const linkClass = isActive
                    ? 'text-text_active_sidebar'
                    : 'text-text_secondary_sidebar';

                  return isHalloweenLink ? `${linkClass} gradient-300 rounded-[5px]` : linkClass;
                }}
                onClick={() => {
                  closeDrawer();
                }}>
                <div className="flex flex-row items-center justify-start w-full py-[5px]">
                  <div className="mr-4 flex justify-center items-center w-[26px] max-h-5">
                    {item.image}
                  </div>
                  <div className="flex flex-row items-center w-full">
                    <p className="text-xs font-bold not-italic uppercase hover:text-text_active_sidebar max-w-[130px]">
                      {t(item.name)}
                    </p>

                    {Boolean(countOfTournaments && item.name === 'menuItem.tournaments') && (
                      <div className="self-center bg-vp_loyalty_arrow_bg rounded-full ml-[5px] 1024:flex items-center justify-center w-[14px] h-[14px]">
                        <p className="pt-[1px] w-full text-center text-[7px] font-bold text-text_color_bage">
                          {countOfTournaments}
                        </p>
                      </div>
                    )}
                    {Boolean(data?.length !== 0 && item.name === 'menuItem.bonuses') && (
                      <div className="self-center bg-vp_loyalty_arrow_bg rounded-full ml-[5px] 1024:flex items-center justify-center w-[14px] h-[14px]">
                        <p className="pt-[1px] w-full text-center text-[7px] font-bold text-text_color_bage">
                          {data?.length}
                        </p>
                      </div>
                    )}
                  </div>
                  {item.path === ROUTE.SHOP.path && (
                    <div className=" rounded bg-vp_loyalty_arrow_bg text-[7px] p-0.5 font-bold text-text_color_bage ml-[5px]">
                      {t('categoryBadgeGame.new')}
                    </div>
                  )}
                </div>
              </NavLink>
            );
          })}
          <div
            className="flex flex-row items-center justify-start w-full pt-[5px] cursor-pointer"
            onClick={() => {
              closeDrawer();
              openChat();
            }}>
            <div className="mr-4 flex justify-center items-center w-[26px]">
              <img src={letter} alt="letter" style={{ maxWidth: '20px' }} />
            </div>
            <p className="text-xs font-bold not-italic uppercase hover:text-text_active_sidebar text-text_secondary_sidebar">
              {t('menuItem.liveChat')}
            </p>
          </div>
        </div>
        <Separator width="w-[100%]" noMarginTop />
        <div className="flex flex-col gap-5 my-[18px] w-full ">
          {Banners.map((banner) => (
            <SidebarBanner
              banner={banner}
              key={banner.id}
              onClick={() => navigateToBannerPage(banner.id === 'driver')}
            />
          ))}
        </div>
        <Separator width="w-[100%]" noMarginTop />
        <div ref={menuRef} className="mt-[18px] flex flex-col items-center gap-[27px] h-[380px]">
          <PWACards />
          <Languages additionalContainerClassName="my-[30px]" handleClick={handeleLanguagesClick} />
        </div>
      </div>
    </>
  );
};
